import React from 'react';
import { MessageSquare, Users, Clock, ThumbsUp, BarChart3, AlertTriangle, Calendar, Lock, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDashboardData } from '../../hooks/useDashboardData';
import { useSubscription } from '../../hooks/useSubscription';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import { useAuth } from '../../contexts/AuthContext';
import MetricCard from '../ui/MetricCard';
import InteractionsChart from '../charts/InteractionsChart';
import ActivityFeed from '../ui/ActivityFeed';
import AdvancedAnalytics from '../ui/AdvancedAnalytics';
import SingleWidgetManager from '../ui/SingleWidgetManager';
import { getPlan } from '../../config/plans';

// Cheapest plan that includes the analytics dashboard (backend PLAN_LIMITS.hasAnalytics).
const ANALYTICS_PLAN = getPlan('basic');
import Loader from '../ui/Loader';
import EmailVerificationBanner from '../ui/EmailVerificationBanner';

const Dashboard = () => {
  const { user } = useAuth();
  const {
    hasActiveSubscription,
    isTrialing,
    isPastDue,
    getPlanName,
    getDaysUntilExpiry,
    getSubscriptionStatus,
    canAccessFeature,
    loading: subLoading
  } = useSubscription();
  const { data, loading } = useDashboardData({ enabled: hasActiveSubscription() });
  const { hasAnalytics } = usePlanFeatures();
  const canExportTranscripts = canAccessFeature('transcript_export');

  const handleExportTranscripts = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/interactions/export.csv`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `paperbrainz-transcripts-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Could not export transcripts. Please try again.');
    }
  };

  if (loading || subLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-96">
        <Loader size="lg" text="Loading Dashboard" />
      </div>
    );
  }

  // Subscription status banner component
  const SubscriptionBanner = () => {
    if (hasActiveSubscription()) {
      // Show trial or expiry warnings
      const daysLeft = getDaysUntilExpiry();

      if (isTrialing()) {
        // Urgency scales with days remaining. >7 days = info, 4-7 = caution,
        // <=3 = warning. Headline copy adapts so a brand-new trial isn't told
        // it's "ending tomorrow."
        const urgent = daysLeft <= 3;
        const soon = daysLeft <= 7;
        const tone = urgent
          ? { bg: "bg-red-500/[0.05] border-red-500/20", chip: "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-500" }
          : soon
            ? { bg: "bg-yellow-500/[0.05] border-yellow-500/20", chip: "bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-500" }
            : { bg: "bg-blue-500/[0.05] border-blue-500/20", chip: "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-500" };
        const headline = daysLeft <= 0
          ? "Trial ends today"
          : daysLeft === 1
            ? "Trial ends tomorrow"
            : soon
              ? `Trial ends in ${daysLeft} days`
              : `${daysLeft} days left on your free trial`;
        const body = soon
          ? "Add a payment method to keep your chatbot live after the trial ends."
          : "No credit card required during the trial. Add one any time to lock in your plan.";
        return (
          <div className="mb-6 sm:mb-8 relative">
            <div className={`${tone.bg} border rounded-[16px] p-4 sm:p-6`}>
              <div className="flex flex-col xs:flex-row items-start gap-3 sm:gap-4">
                <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-[14px] flex items-center justify-center ${tone.chip}`}>
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-1">{headline}</h3>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mb-3 sm:mb-4 font-light">{body}</p>
                  <Link
                    to="/dashboard/billing"
                    className="inline-flex items-center text-xs sm:text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full transition-colors"
                  >
                    {soon ? "Add payment method" : "Upgrade plan"}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        );
      }

      if (isPastDue()) {
        return (
          <div className="mb-6 sm:mb-8 relative">
            <div className="bg-red-500/[0.05] dark:bg-red-500/[0.05] border border-red-500/20 rounded-[16px] p-4 sm:p-6">
              <div className="flex flex-col xs:flex-row items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-red-100 dark:bg-red-900/20 rounded-[14px] flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-700 dark:text-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-1">
                    Payment required
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mb-3 sm:mb-4 font-light">
                    Update your payment method to continue using premium features.
                  </p>
                  <Link
                    to="/dashboard/billing"
                    className="inline-flex items-center text-xs sm:text-sm font-medium bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full transition-colors"
                  >
                    Update payment
                  </Link>
                </div>
              </div>
            </div>
          </div>
        );
      }

      return null; // Active subscription, no banner needed
    }

    // No active subscription
    return (
      <div className="mb-6 sm:mb-8 relative">
        <div className="dark:bg-white/[0.02] dark:border-white/[0.06] bg-gray-50 border border-gray-200 rounded-[20px] p-5 sm:p-6 md:p-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                Upgrade to unlock all features
              </h3>
            </div>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500 mb-4 sm:mb-6 leading-relaxed font-light">
              Get access to website integration, knowledge sources, custom branding, and advanced analytics to supercharge your customer support.
            </p>
            <div className="flex flex-col xs:flex-row items-start xs:items-center gap-3">
              <Link
                to="/dashboard/billing"
                className="w-full xs:w-auto inline-flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 py-2 rounded-full text-xs sm:text-sm transition-colors"
              >
                View plans
              </Link>
              <Link
                to="/dashboard/integration"
                className="w-full xs:w-auto inline-flex items-center justify-center text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white font-medium text-xs sm:text-sm transition-colors"
              >
                See features →
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-mobile py-4 sm:py-6 md:py-8 max-w-7xl mx-auto">
        {/* Email Verification Banner */}
        {user && !user.emailVerified && (
          <EmailVerificationBanner email={user.email} />
        )}

        {/* Subscription Status Banner */}
        <SubscriptionBanner />

        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8 md:mb-10">
          <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-gray-900 dark:text-white">Welcome back</h1>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-500 mt-1 font-light">Here's what's happening with your AI customer service today.</p>
        </div>

        {/* Metrics Grid - Gated for Pro users */}
        {hasAnalytics ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8 md:mb-12">
              <MetricCard
                title="Total Interactions"
                value={data.metrics.totalInteractions}
                change={data.metrics.totalInteractionsChange}
                icon={MessageSquare}
              />
              <MetricCard
                title="Active Chats"
                value={data.metrics.activeChats}
                change={data.metrics.activeChatChange}
                icon={Users}
              />
              <MetricCard
                title="Avg Response Time"
                value={Math.round(data.metrics.responseTime / 1000)} // Convert ms to seconds
                change={data.metrics.responseTimeChange}
                icon={Clock}
                format="time"
              />
              {/* TODO: Re-enable satisfaction rate card later
              <MetricCard
                title="Satisfaction Rate"
                value={data.metrics.satisfactionRate}
                change={data.metrics.satisfactionRateChange}
                icon={ThumbsUp}
                format="percentage"
              />
              */}
            </div>

            {/* Charts and Analytics */}
            <div className="mb-6 sm:mb-8 md:mb-12">
              <div className="dark:bg-white/[0.02] dark:border-white/[0.06] bg-white border border-gray-200 rounded-[20px] overflow-hidden">
                <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 border-b dark:border-white/[0.06] border-gray-100">
                  <div className="flex items-start sm:items-center gap-3">
                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 flex-shrink-0 mt-1 sm:mt-0" />
                    <div className="min-w-0">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Analytics Overview</h3>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mt-0.5 sm:mt-1 font-light">Track your customer interaction trends and AI performance metrics</p>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6 md:p-8">
                  <InteractionsChart data={data.chartData} />
                </div>
              </div>
            </div>
          </>
        ) : (
          /* Upgrade prompt for Basic users */
          <div className="mb-6 sm:mb-8 md:mb-12">
            <div className="dark:bg-white/[0.02] dark:border-white/[0.06] bg-gray-50 border border-gray-200 rounded-[20px] p-6 sm:p-8 md:p-12">
              <div className="text-center max-w-2xl mx-auto">
                <div className="w-12 h-12 sm:w-16 sm:h-16 dark:bg-white/[0.06] bg-gray-100 rounded-[14px] flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <Lock className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500 dark:text-gray-400" />
                </div>
                {/* This panel is gated on hasAnalytics, which is false only on Starter
                    (backend PLAN_LIMITS). The upsell is therefore Basic, not Pro — it
                    previously said "Pro Feature ... Start at $124/month", which named the
                    wrong plan at a price that does not exist. Price comes from
                    src/config/plans.js so it cannot drift again. */}
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                  Analytics dashboard — included on {ANALYTICS_PLAN.name}
                </h3>
                <p className="text-sm sm:text-base md:text-lg text-gray-500 dark:text-gray-500 mb-6 sm:mb-8 leading-relaxed font-light">
                  Unlock detailed analytics including Total Interactions, Active Chats, Response Times,
                  and interactive charts to track your AI assistant's performance.
                </p>
                <div className="flex flex-col xs:flex-row items-center justify-center gap-3 sm:gap-4">
                  <Link
                    to="/dashboard/billing"
                    className="w-full xs:w-auto inline-flex items-center justify-center bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 sm:px-6 py-2.5 sm:py-3 rounded-full text-sm sm:text-base transition-colors"
                  >
                    Upgrade to {ANALYTICS_PLAN.name}
                  </Link>
                </div>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mt-4 sm:mt-6 font-light">
                  ${ANALYTICS_PLAN.monthlyPrice}/month • {ANALYTICS_PLAN.limits.conversations.toLocaleString()} conversations • Transcript export
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Activity and Widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8 md:mb-12">
          {/* Recent Activity - Pro Feature */}
          {hasAnalytics ? (
            <ActivityFeed
              activities={data.recentActivities}
              canExport={canExportTranscripts}
              onExport={handleExportTranscripts}
            />
          ) : (
            <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] overflow-hidden">
              <div className="p-6">
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-[14px] bg-gray-50 dark:bg-white/[0.04] flex items-center justify-center mx-auto mb-6">
                    <Lock className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Recent Activity
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-sm mx-auto">
                    View real-time customer interactions and conversation history with the Pro plan.
                  </p>
                  <Link
                    to="/dashboard/billing"
                    className="inline-flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-full font-medium transition-all duration-200"
                  >
                    <span>Upgrade to Pro</span>
                  </Link>
                </div>
              </div>
            </div>
          )}
          <SingleWidgetManager widget={data.widgets?.[0]} />
        </div>

        {/* Advanced Analytics (Pro) */}
        <div className="mb-6 sm:mb-8 md:mb-12">
          <AdvancedAnalytics canAccess={canAccessFeature('advanced_analytics')} />
        </div>

    </div>
  );
};

export default Dashboard;
